// Colour palette editor — visual wheel + HEX/RGB/HSL inputs + harmony generator
// + live preview of the palette on charter-like elements (headings, table, cards).
import React, { useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wand2 } from "lucide-react";
import { PALETTE_SLOTS, hexToRgb, rgbToHex, hexToHsl, hslToHex, harmonyFromPrimary } from "@/lib/colorUtils";

const clampNum = (v, lo, hi) => Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : 0));

export default function PaletteEditor({ value, onChange, testid = "palette" }) {
  const [slot, setSlot] = useState("primary");
  const pal = { ...value };
  const current = pal[slot] || "#000000";
  const rgb = useMemo(() => hexToRgb(current), [current]);
  const hsl = useMemo(() => hexToHsl(current), [current]);

  const setColor = (hex) => onChange({ ...pal, [slot]: hex });
  const setRgb = (k, v) => setColor(rgbToHex({ ...rgb, [k]: clampNum(v, 0, 255) }));
  const setHsl = (k, v) => {
    const lim = k === "h" ? 360 : 100;
    setColor(hslToHex({ ...hsl, [k]: clampNum(v, 0, lim) }));
  };

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-5" data-testid={testid}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-1.5" data-testid={`${testid}-slots`}>
          {PALETTE_SLOTS.map(s => (
            <button key={s.key} type="button" onClick={() => setSlot(s.key)}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${slot === s.key ? "border-emerald-700 bg-emerald-50" : "border-border hover:bg-muted/50"}`}
              data-testid={`${testid}-slot-${s.key}`}>
              <span className="w-5 h-5 rounded-full border border-black/10 shrink-0" style={{ background: pal[s.key] }} />
              <span className="truncate">{s.label}</span>
            </button>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="w-full"
          onClick={() => onChange({ ...pal, ...harmonyFromPrimary(pal.primary || "#166B3F") })}
          data-testid={`${testid}-harmonize`}>
          <Wand2 className="w-4 h-4 mr-1.5" /> Generate palette from Primary
        </Button>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <HexColorPicker color={current} onChange={setColor} style={{ width: 200, height: 200 }} data-testid={`${testid}-wheel`} />
          <div className="flex-1 space-y-3">
            <div>
              <Label className="text-xs font-semibold">{PALETTE_SLOTS.find(s => s.key === slot)?.label} — HEX</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-9 h-9 rounded-md border border-black/10 shrink-0" style={{ background: current }} data-testid={`${testid}-swatch`} />
                <Input value={current} onChange={e => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && setColor(e.target.value)} className="font-mono w-32" data-testid={`${testid}-hex`} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">RGB</Label>
              <div className="flex gap-1.5 mt-1">
                {["r", "g", "b"].map(k => (
                  <Input key={k} type="number" min="0" max="255" value={rgb[k]} onChange={e => setRgb(k, e.target.value)} className="w-20" data-testid={`${testid}-rgb-${k}`} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">HSL</Label>
              <div className="flex gap-1.5 mt-1">
                {["h", "s", "l"].map(k => (
                  <Input key={k} type="number" min="0" max={k === "h" ? 360 : 100} value={hsl[k]} onChange={e => setHsl(k, e.target.value)} className="w-20" data-testid={`${testid}-hsl-${k}`} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* live preview of the palette on charter-like elements */}
        <div className="rounded-lg border p-4" style={{ background: pal.background, borderColor: pal.border }} data-testid={`${testid}-live-preview`}>
          <div className="h-1.5 rounded-full mb-3" style={{ background: pal.accent }} />
          <div className="text-lg font-bold" style={{ color: pal.heading, fontFamily: "'Anek Malayalam', sans-serif" }}>സമിതി അംഗങ്ങൾ · Section Heading</div>
          <p className="text-sm mt-1" style={{ color: pal.text }}>Body text in the selected colour scheme — പൗരാവകാശ രേഖ body text.</p>
          <p className="text-xs mt-0.5" style={{ color: pal.muted }}>Muted helper text · Local Body name</p>
          <div className="mt-3 rounded-md overflow-hidden border" style={{ borderColor: pal.border }}>
            <div className="px-3 py-1.5 text-xs font-bold" style={{ background: pal.table_header, color: "#ffffff" }}>പേര് · Name &nbsp;|&nbsp; ഫോൺ · Phone</div>
            <div className="px-3 py-1.5 text-xs" style={{ color: pal.text }}>Row one</div>
            <div className="px-3 py-1.5 text-xs" style={{ background: pal.highlight, color: pal.text }}>Row two (highlighted)</div>
          </div>
          <div className="mt-3 flex gap-2">
            <span className="text-xs font-semibold rounded-md px-3 py-1.5" style={{ background: pal.primary, color: "#fff" }}>Primary action</span>
            <span className="text-xs font-semibold rounded-md px-3 py-1.5" style={{ background: pal.secondary, color: "#fff" }}>Secondary</span>
            <span className="text-xs font-semibold rounded-md px-3 py-1.5 border" style={{ borderColor: pal.accent, color: pal.heading }}>Accent outline</span>
          </div>
        </div>
      </div>
    </div>
  );
}
